import {
  type DeliveryProposalTableDataType,
  getDraftOfferCargoRows,
} from "@/pages/shipper-cargo-detail/ui/utils";
import { formatMoney } from "@/shared/lib/currency";
import { toLocaleTextAsDateTime } from "@/shared/lib/datetime";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseTable } from "@/shared/ui/base-table";
import type { Column } from "@/shared/ui/base-table/types";
import { OfferCargoStatusBadge } from "@/widgets/offer-cargo-status-badge";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useNavigate } from "@tanstack/react-router";
import type React from "react";
import { useMemo } from "react";
import type { AdminCargoDetailPageProps } from "../model";

export type DeliveryProposalsInfoProps = {
  offerCargoProcess: AdminCargoDetailPageProps["offerCargoProcess"];
  draftOfferCargoId?: number;
};

export const DeliveryProposalsInfo: React.FC<DeliveryProposalsInfoProps> = ({
  offerCargoProcess,
  draftOfferCargoId,
}) => {
  const navigate = useNavigate();
  const SuggestTable = BaseTable<DeliveryProposalTableDataType>;

  const columns: Column<DeliveryProposalTableDataType>[] = [
    {
      key: "companyName",
      label: "企業名",
      cell: (row) => {
        return <Typography color={grey[900]}>{row.companyName}</Typography>;
      },
    },
    {
      key: "deliveryProposalContractStatus",
      label: "契約状況",
      cell: (row) => {
        return (
          <OfferCargoStatusBadge
            statusType="delivery_proposal_contract"
            status={row.deliveryProposalContractStatus}
          />
        );
      },
    },
    {
      key: "companyName",
      label: "返答期限",
      cell: (row) => {
        return (
          <Typography color={grey[900]}>
            {toLocaleTextAsDateTime(row.responseDeadlineAt)}
          </Typography>
        );
      },
    },
    {
      key: "proposedAt",
      label: "提案日時",
      cell: (row) => {
        return <Typography color={grey[900]}>{row.proposedAt}</Typography>;
      },
    },
    {
      key: "requiredShippingFee",
      label: "運賃",
      cell: (row) => {
        const requiredShippingFee = row.requiredShippingFee;
        const proposedFeeDiff = row.proposedFeeDiff;
        return (
          <Stack spacing={0} alignItems="end">
            <Typography variant="body1">
              {formatMoney(requiredShippingFee)}
            </Typography>
            {proposedFeeDiff !== 0 && proposedFeeDiff !== undefined && (
              <Typography variant="caption">
                {proposedFeeDiff > 0 ? "+" : "-"}
                {formatMoney(Math.abs(proposedFeeDiff))}
              </Typography>
            )}
          </Stack>
        );
      },
    },
  ];

  const rows = useMemo(
    () => getDraftOfferCargoRows(offerCargoProcess),
    [offerCargoProcess],
  );

  const handleCreateClick = () => {
    if (draftOfferCargoId) {
      navigate({
        to: "/admin/cargos/$id/delivery-proposals/create",
        params: { id: draftOfferCargoId.toString() },
      });
    }
  };

  const createButton = draftOfferCargoId ? (
    <BaseButton
      variant="contained"
      size="small"
      startIcon={<BaseIcon name="plus" htmlColor="white" />}
      onClick={handleCreateClick}
      sx={{
        fontSize: "14px",
      }}
    >
      配送提案を新規作成
    </BaseButton>
  ) : null;

  if (offerCargoProcess.status === "loading") {
    return (
      <Stack spacing={3} id="delivery-proposal-list">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h2" fontSize="18px" fontWeight={700}>
            配送提案
          </Typography>
          {createButton}
        </Stack>
        <CircularProgress />
      </Stack>
    );
  }
  if (offerCargoProcess.status === "error") {
    return (
      <Stack spacing={3} id="delivery-proposal-list">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h2" fontSize="18px" fontWeight={700}>
            配送提案
          </Typography>
          {createButton}
        </Stack>
        <Typography>{offerCargoProcess.error.message}</Typography>
      </Stack>
    );
  }
  const deliveryProposalOfferCargos =
    offerCargoProcess.data.deliveryProposalOfferCargos;
  return (
    <Stack spacing={3} id="delivery-proposal-list">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h2" fontSize="18px" fontWeight={700}>
          {`配送提案${deliveryProposalOfferCargos ? ` (${deliveryProposalOfferCargos.length}件)` : ""}`}
        </Typography>
        {createButton}
      </Stack>
      <SuggestTable
        columns={columns}
        rows={rows}
        to="/admin/delivery-proposals/$id"
        params={(row) => ({
          id: row.id,
        })}
        noDataMessage="現在、提案がございません。"
      />
    </Stack>
  );
};
